/**
 * PWA Service Worker Registrierung
 * 
 * Registriert den Service Worker für Offline-Support,
 * Background Sync und installierbare PWA.
 */

export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    console.log('Service Workers werden von diesem Browser nicht unterstützt.');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
    });

    console.log('✅ Service Worker registriert:', registration.scope);

    // SW Update: Automatischer Reload wenn neue Version aktiviert wird
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SW_UPDATED') {
        console.log('🔄 Service Worker Update erkannt (v' + event.data.version + ') – Seite wird neu geladen');
        window.location.reload();
      }
    });

    // URS-150: Encryption-Key an SW senden (PBKDF2 vom User-Passwort)
    await sendEncryptionKeyToSW();

    // Auf Update warten
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('📦 Neues Update verfügbar – Seite neu laden für aktivierung');
          }
        });
      }
    });
  } catch (error) {
    console.error('❌ Service Worker Registrierung fehlgeschlagen:', error);
  }
}

/**
 * URS-150: Sendet den AES-256-GCM Encryption Key an den Service Worker
 * für verschlüsselte Offline-Speicherung.
 */
export async function sendEncryptionKeyToSW(): Promise<void> {
  const stored = localStorage.getItem('rotkreuz-auth');
  if (!stored) return;

  try {
    const parsed = JSON.parse(stored);
    const token = parsed?.state?.token;
    if (!token) return;

    // Token als Basis für PBKDF2-Schlüsselableitung (wie in lib/crypto.ts)
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw', enc.encode(token),
      { name: 'PBKDF2' }, false, ['deriveBits', 'deriveKey']
    );
    const rawKey = await crypto.subtle.deriveBits(
      { name: 'PBKDF2', salt: enc.encode('rotkreuz-ds-offline'), iterations: 100000, hash: 'SHA-256' },
      keyMaterial, 256
    );

    const keyHex = Array.from(new Uint8Array(rawKey)).map(b => b.toString(16).padStart(2, '0')).join('');

    if (navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({
          type: 'SET_ENCRYPTION_KEY',
          rawKey: keyHex,
        });
      } catch(e) { /* No Listener - ok */ }
    } else {
      // SW noch nicht bereit — beim 'controllerchange' nachholen
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (navigator.serviceWorker.controller) {
          try {
            navigator.serviceWorker.controller.postMessage({
              type: 'SET_ENCRYPTION_KEY',
              rawKey: keyHex,
            });
          } catch(e) { /* No Listener - ok */ }
        }
      });
    }
  } catch {
    console.warn('Konnte Encryption Key nicht an Service Worker senden');
  }
}

/**
 * Prüft ob das Gerät online ist
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Prüft ob die App als PWA installiert werden kann
 */
export function canInstallAsPWA(): boolean {
  return 'BeforeInstallPromptEvent' in window;
}
